import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, KeyRound } from "lucide-react";
import { AuthLayout, Field } from "@/components/auth/AuthLayout";
import { api, formatApiError } from "@/api/client";

export default function ResetPassword() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const navigate = useNavigate();

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null);
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 1500);
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title={t("auth.reset.title")} backHref="/login" backLabelKey="auth.back_to_login">
            {!done ? (
                <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                    {!token && <div className="text-sm" style={{ color: "var(--error-fg)" }}>{t("auth.reset.no_token")}</div>}
                    <Field label={t("auth.new_password")}>
                        <input data-testid="reset-password-input" required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </Field>
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }} data-testid="reset-error">{err}</div>}
                    <button data-testid="reset-submit" type="submit" disabled={loading || !token} className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                        {t("auth.reset.submit")}
                    </button>
                    <Link to="/login" className="text-sm hover:underline block text-center" style={{ color: "var(--accent)" }}>{t("auth.back_to_login")}</Link>
                </form>
            ) : (
                <div data-testid="reset-done" className="flex items-start gap-2 rounded-md p-4" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }}>
                    <CheckCircle2 size={18} />
                    <span className="text-sm">{t("auth.reset.done")}</span>
                </div>
            )}
        </AuthLayout>
    );
}
